import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from "react-helmet";
import ApiUtil from '../../utils/apiUtils';
import BlogRenderer from '../BlogRenderer';
import FusionContentRenderer from '../FusionContentRenderer';
import '@fortawesome/fontawesome-free/css/all.min.css';
// CSS modules ensure styles are only applied to this component
//import styles from './BlogDetail.module.css';
// This shared CSS will be loaded only when this component is loaded
import './blog-styles.css';

const BlogDetail = () => {
    const { slug } = useParams();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchBlog = useCallback(async () => {
        try {
            const response = await ApiUtil.SendAsync({
                url: `/blogs/${slug}`,
                method: 'GET'
            });
            
            // Check if this is a page (not a post) and prevent access
            if (response.data && response.data.postType === 'page') {
                setError('Page not found');
                setLoading(false);
                return;
            }
            
            setBlog(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching blog:', error);
            setError('Blog not found');
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        fetchBlog();
    }, [fetchBlog]);

    // Helper function to get the correct image source URL
    const getImageSrc = (imageUrl) => {
        // If it's already an absolute URL (S3, etc.), use it as is
        if (imageUrl && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))) {
            return imageUrl;
        }
        // Otherwise, it's a local file path, prepend base URL
        return `https://hdmovietoday.xyz${imageUrl}`;
    };

    if (loading) {
        return (
            <div>
                <div className="container">
                    <div className="row">
                        <div className="col-md-12 text-center py-5">
                            <div className="text-xl">Loading blog...</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !blog) {
        return (
            <div>
                <div className="container">
                    <div className="row">
                        <div className="col-md-12 text-center py-5">
                            <h2 className="mb-4"><b>Blog Not Found</b></h2>
                            <p className="mb-4">The blog post you're looking for doesn't exist.</p>
                            <Link
                                to="/blogs"
                                className="btn btn-primary"
                                style={{ backgroundColor: '#e67929', borderColor: '#e67929' }}
                            >
                                Back to Blogs
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const parsedContent = blog.body ? blog.body : { blocks: [] };

    // Function to detect if content is Fusion Builder
    const isFusionBuilderContent = () => {
        const contentType = blog.contentType || 'editor-js';
        
        // Check if explicitly marked as fusion-builder
        if (contentType === 'fusion-builder' && blog.rawHtml) {
            return { isFusion: true, content: blog.rawHtml };
        }
        
        // Check if it's HTML content
        if (contentType === 'html' && blog.rawHtml) {
            return { isFusion: true, content: blog.rawHtml };
        }
        
        // Check if misclassified Editor.js content contains Fusion Builder markers
        if (parsedContent && typeof parsedContent === 'object' && parsedContent.blocks && parsedContent.blocks.length > 0) {
            const firstBlock = parsedContent.blocks[0];
            if (firstBlock && firstBlock.type === 'paragraph' && firstBlock.data && firstBlock.data.text) {
                const content = firstBlock.data.text;
                if (content.includes('fusion-builder-container') || content.includes('fusion-builder-row') || content.includes('fusion-column')) {
                    return { isFusion: true, content: content };
                }
            }
        }
        
        return { isFusion: false, content: null };
    };

    const fusionCheck = isFusionBuilderContent();

    // If it's Fusion Builder content, render only the raw HTML without any blog template
    if (fusionCheck.isFusion) {
        return (
            <div>
                <Helmet>
                    <title>
                        {blog.title} | Flooring Deals
                    </title>
                    <meta
                        name="description"
                        content={blog.metaDescription || `Read about ${blog.title}`}
                    />
                    <meta name="keywords" content={blog.metaKeywords}></meta>
                </Helmet>

                {/* Use the same container structure as the rest of the site */}
                <div className="about-us-area pb-100 post-content blog-page">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                {/* Just render the raw HTML */}
                                <FusionContentRenderer htmlContent={fusionCheck.content} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Helmet>
                <title>
                    {blog.title} | Flooring Deals - Blog
                </title>
                <meta
                    name="description"
                    content={blog.metaDescription || `Read our blog post about ${blog.title}`}
                />
                <meta name="keywords" content={blog.metaKeywords}></meta>
            </Helmet>

            {/* Breadcrumb - Hidden on Mobile */}
            <div className=".blog-page breadcrumb mb-0 d-none d-md-block">
                <div className="container">
                    <ul>
                        <li>
                            <a href="/">Home</a>
                        </li>
                        <li>
                            {'>'}
                        </li>
                       
                        <li>
                            <a href="/blogs">Blogs</a>
                        </li>
                        <li>
                            {'>'}
                        </li>
                       
                        <li>{blog.title}</li>
                    </ul>
                </div>
            </div>

            {/* Blog Detail Content */}
            <div className="about-us-area pb-100 post-content blog-page">
                <div className="container">
                    <div className="row">
                        <div className="col-md-12">
                            {/* Back Button */}
                            <div className="mb-4">
                                <Link
                                    to="/blogs"
                                    className="btn"
                                    style={{ color: '#e67929' }}
                                >
                                    <i className="fas fa-arrow-left me-2"></i>
                                    Back to All Blogs
                                </Link>
                            </div>

                            {/* Blog Content Card */}
                            <div className="card mb-5">
                                {/* Featured Image */}
                                {blog.featuredImage && (
                                    <div className="position-relative">
                                        <img
                                            src={getImageSrc(blog.featuredImage)}
                                            alt={blog.title}
                                            className="card-img-top"
                                            style={{ maxHeight: '500px', objectFit: 'cover' }}                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.innerHTML = `
                                            <div style="height: 300px; display: flex; align-items: center; justify-content: center; background-color: #f8f9fa;">
                                                <div class="text-center">
                                                    <i class="fas fa-image fa-3x text-muted"></i>
                                                    <p class="mt-2 text-muted">Image unavailable</p>
                                                </div>
                                            </div>
                                        `;
                                    }}
                                        />
                                    </div>
                                )}

                                {/* Blog Header */}
                                <div className="card-body">
                                    {/* Meta Information */}
                                    <div className="mb-4">
                                        <span className="text-muted me-3">
                                            <i className="fas fa-calendar-alt me-2" style={{ color: '#e67929' }}></i>
                                            {new Date(blog.createdAt).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </span>
                                    </div>

                                    {/* Title */}
                                    <h1><b>{blog.title}</b></h1>

                                    {/* Excerpt */}
                                    {blog.excerpt && (
                                        <div className="p-4 my-4" style={{ background: '#f8f9fa', borderLeft: '4px solid #e67929' }}>
                                            <p className="lead mb-0">
                                                {blog.excerpt}
                                            </p>
                                        </div>
                                    )}

                                    {/* Blog Content */}
                                    <div className="blog-content mt-4">
                                        {(() => {
                                            if (parsedContent && typeof parsedContent === 'object' && parsedContent.blocks && parsedContent.blocks.length > 0) {
                                                // Render Editor.js content
                                                return <BlogRenderer data={parsedContent} />;
                                            } else if (parsedContent && typeof parsedContent === 'string') {
                                                // If blog.body is a string, treat as HTML content
                                                return <FusionContentRenderer htmlContent={parsedContent} />;
                                            } else {
                                                // Fallback content
                                                return (
                                                    <div className="text-center py-5">
                                                        <div style={{ width: '64px', height: '64px', background: 'linear-gradient(to right, #e67929, #ff8a36)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                                                            <i className="fas fa-file-alt fa-2x text-white"></i>
                                                        </div>
                                                        <h3><b>Content Coming Soon</b></h3>
                                                        <p className="text-muted">This blog post is being prepared. Check back soon for the full content!</p>
                                                    </div>
                                                );
                                            }
                                        })()}
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="text-center pt-4 mt-5 border-top">
                                <h3 className="mb-4"><b>Explore More</b></h3>
                                <p className="mb-4">Discover more insights and tips from our flooring experts</p>
                                <Link
                                    to="/blogs"
                                    className="btn btn-lg"
                                    style={{ backgroundColor: '#e67929', color: 'white' }}
                                >
                                    <i className="fas fa-book me-2"></i>
                                    View All Blogs
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlogDetail;
